import { LEGAL, PLAN_META, PLANS, TRIAL_DAYS, TRIAL_PACK } from '@str/shared';
import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import { ImportForm } from '@/components/ImportForm';
import { Logo } from '@/components/ui';
import { money } from '@/lib/format';
import { useMe } from '@/lib/queries';

// Real frames from Reels the product made for the owner's own cabins (Twin Lakes, Colorado and a
// Smoky Mountains cabin), cropped above the burned-in captions. Files live in public/examples.
const HERO = [
  {
    src: '/examples/reel.jpg',
    alt: 'A lakefront log cabin at sunset, from a Reel',
    aspect: '9/16',
    rot: '-9deg',
    x: 0,
    y: 40,
    z: 1,
    just: 'flex-start',
    text: 'POV: you booked lakefront.',
    tag: 'Reel · 0:14',
    delay: '.1s',
  },
  {
    src: '/examples/picture.jpg',
    alt: 'A log cabin great room with floor-to-ceiling windows onto a mountain lake',
    aspect: '4/5',
    rot: '1deg',
    x: 165,
    y: 120,
    z: 3,
    just: 'flex-end',
    text: 'Slow mornings, on purpose.',
    tag: 'Image · 4:5',
    delay: '.25s',
  },
  {
    src: '/examples/carousel.jpg',
    alt: 'A covered cabin porch looking over wooded mountains at sunset',
    aspect: '4/5',
    rot: '9deg',
    x: 320,
    y: 20,
    z: 2,
    just: 'flex-start',
    text: '“Quietest place we’ve stayed.”',
    tag: 'Carousel · 1/5',
    delay: '.4s',
  },
] as const;

const CHEAPEST_MONTHLY_CENTS = Math.min(...PLANS.map((k) => PLAN_META[k].priceCents));
const PRICE_LINE = `${money(TRIAL_PACK.priceCents)} for a ${TRIAL_DAYS}-day trial with your first post and Reel included · then from ${money(CHEAPEST_MONTHLY_CENTS)} a month per property · cancel anytime`;

export function Landing() {
  const me = useMe();
  const loggedIn = !!me.data?.user;

  return (
    <div className="screen landing-page">
      <div className="topbar">
        <Logo />
        <div className="landing-account" style={{ fontSize: 13, color: '#777' }}>
          {loggedIn ? (
            <Link to="/app" style={{ fontWeight: 600 }}>
              Back to the app →
            </Link>
          ) : (
            <>
              <span className="landing-account-prompt">Have an account? </span>
              <Link to="/login" style={{ fontWeight: 600 }}>
                Log in
              </Link>
            </>
          )}
        </div>
      </div>
      <div
        className="grid-md-1 landing-hero"
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: 'minmax(440px,560px) 1fr',
          gap: 40,
          padding: '0 clamp(16px, 3vw, 40px) 60px clamp(16px, 6vw, 80px)',
          alignItems: 'center',
          maxWidth: 1280,
          width: '100%',
          margin: '0 auto',
          minHeight: 620,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 28,
            minWidth: 0,
            animation: 'rise .6s both',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h1
              style={{
                fontSize: 56,
                lineHeight: 1,
                fontWeight: 800,
                letterSpacing: '-.04em',
                textWrap: 'balance',
              }}
            >
              Your listing already has everything a month of posts needs.
            </h1>
            <p style={{ fontSize: 17, color: '#666', lineHeight: 1.5, maxWidth: 460 }}>
              Paste one link. We pull your photos and description, draft the posts, and you approve.
            </p>
          </div>
          <ImportForm loggedIn={loggedIn} />
          <p style={{ fontSize: 13, color: '#666', lineHeight: 1.5, marginTop: -16 }}>
            {PRICE_LINE}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: '#777',
              flexWrap: 'wrap',
            }}
          >
            <span>Works with</span>
            {['Airbnb', 'VRBO', 'Booking.com', 'Direct site'].map((c) => (
              <span
                key={c}
                style={{
                  padding: '5px 10px',
                  borderRadius: 8,
                  background: '#fff',
                  border: '1px solid rgba(0,0,0,.08)',
                  fontWeight: 600,
                  color: '#333',
                }}
              >
                {c}
              </span>
            ))}
          </div>
        </div>
        {/* Fanned out beside the hero on desktop; a row below it on smaller screens (R25). */}
        <div className="landing-examples">
          {HERO.map((h) => (
            <div
              key={h.tag}
              className="landing-example"
              style={
                {
                  '--x': `${h.x}px`,
                  '--y': `${h.y}px`,
                  '--rot': h.rot,
                  zIndex: h.z,
                  aspectRatio: h.aspect,
                  animationDelay: h.delay,
                } as CSSProperties
              }
            >
              <img
                src={h.src}
                alt={h.alt}
                decoding="async"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    h.just === 'flex-start'
                      ? 'linear-gradient(180deg,rgba(0,0,0,.45),rgba(0,0,0,0) 55%)'
                      : 'linear-gradient(0deg,rgba(0,0,0,.5),rgba(0,0,0,0) 55%)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: h.just,
                  gap: 10,
                  padding: 16,
                }}
              >
                <span className="tag" style={{ alignSelf: 'flex-start' }}>
                  {h.tag}
                </span>
                <span
                  className="landing-example-text"
                  style={{
                    color: '#fff',
                    fontWeight: 700,
                    letterSpacing: '-.025em',
                    lineHeight: 1.1,
                    textShadow: '0 2px 14px rgba(0,0,0,.35)',
                  }}
                >
                  {h.text}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <footer
        style={{
          display: 'flex',
          gap: 20,
          justifyContent: 'center',
          flexWrap: 'wrap',
          padding: '20px 16px 28px',
          fontSize: 13,
          color: '#777',
          borderTop: '1px solid rgba(0,0,0,.06)',
        }}
      >
        {loggedIn ? <Link to="/app">Back to the app</Link> : <Link to="/login">Log in</Link>}
        <a href={LEGAL.privacy}>Privacy</a>
        <a href={LEGAL.terms}>Terms</a>
      </footer>
    </div>
  );
}
